import {
  employeeInfoFields,
  profileFieldOptions,
  calendarSharingFields,
} from '../../../shared/shared-profile-field-filter';
import {
  triggerGroupUiOptions,
  getEventUiGroup,
} from './trigger-event-options';
import { documentTypeOptions } from '../../../shared/google-document-types';

const fieldNameMap: Record<string, string> = {
  // Drive filters
  fileType: 'File Type',
  fileSize: 'File Size',
  fileSharedToUserExternalToDomain: 'user external to the domain',
  fileSharedToUserInternalToDomain: 'user internal to the domain',
  fileIsSharedPublic: 'anyone with the link (public)',
  fileSharedToSpecificUser: 'specific user',
  fileIsSharedToDomain: 'domain',
  // Group filters
  groupName: 'Group Name',
  memberCount: 'Member Count',
  groupEmail: 'Group Email',
};

export type ConditionSummaryItem = {
  text: string;
  logicalOperator: string;
};

interface RuleSummaryGeneratorParams {
  triggerSources: string[];
  allSource?: boolean;
  triggerGroup?: string;
  triggerEvent?: string;
  occursEverytime?: boolean;
  conditions?: ConditionSummaryItem[];
  actions?: string[];
}

function formatList(
  items: string[],
  conjunction: 'and' | 'or' = 'and'
): string {
  if (items.length === 0) return '';
  if (items.length === 1) return items[0];
  if (items.length === 2) {
    return `${items[0]} ${conjunction} ${items[1]}`;
  }

  return `${items.slice(0, -1).join(', ')}, ${conjunction} ${
    items[items.length - 1]
  }`;
}

function formatSources(sources: string[], visibleCount = 2): React.ReactNode {
  if (sources.length === 0) return '';

  if (sources.length <= visibleCount) {
    return formatList(sources, 'or');
  }

  const visibleSources = sources.slice(0, visibleCount);
  const remainingCount = sources.length - visibleCount;

  return (
    <>
      {formatList(visibleSources, 'or')}{' '}
      <strong>(+{remainingCount} more trigger sources)</strong>
    </>
  );
}

function formatConditions(
  conditions: ConditionSummaryItem[],
  visibleCount = 2
): React.ReactNode {
  if (conditions.length === 0) return '';

  const joinConditions = (items: ConditionSummaryItem[]) => {
    return items
      .map((item, index) => {
        if (index === items.length - 1) return item.text;
        const op = (item.logicalOperator || 'and').toLowerCase();
        return `${item.text} ${op} `;
      })
      .join('');
  };

  if (conditions.length <= visibleCount) {
    return <>{joinConditions(conditions)}</>;
  }

  const visibleConditions = conditions.slice(0, visibleCount);
  const remainingCount = conditions.length - visibleCount;

  return (
    <>
      {joinConditions(visibleConditions)}{' '}
      <strong>(+{remainingCount} more conditions)</strong>
    </>
  );
}

function formatActions(actions: string[]): string {
  if (actions.length === 0) {
    return 'perform the configured actions';
  }
  const lowerCaseActions = actions.map((action) => action.toLowerCase());

  return formatList(lowerCaseActions, 'and');
}

export function generateRuleSummary({
  triggerSources,
  allSource = false,
  triggerGroup,
  triggerEvent,
  occursEverytime = false,
  conditions = [],
  actions = [],
}: RuleSummaryGeneratorParams): React.ReactNode {
  const sourceText = allSource
    ? 'all users'
    : triggerSources.length > 0
    ? formatSources(triggerSources, 2)
    : 'the configured source';

  const resolvedTriggerGroup =
    triggerEvent && getEventUiGroup(triggerEvent) === 'groupEvents'
      ? 'groupEvents'
      : triggerGroup;

  const groupLabel =
    triggerGroupUiOptions.find((t) => t.value === resolvedTriggerGroup)
      ?.displayValue || '';

  const eventLabel = triggerEvent || 'the configured event';

  const occurrenceText = occursEverytime
    ? 'every time'
    : 'when the threshold is reached';

  const actionText = formatActions(actions);

  return (
    <>
      When {sourceText} triggers an {groupLabel}: {eventLabel}, {occurrenceText}
      {conditions.length > 0 && (
        <>, and only if {formatConditions(conditions)}</>
      )}
      , the system will {actionText}.
    </>
  );
}

export function getConditionSummaryItems(
  conditionBlocks: any[] = []
): ConditionSummaryItem[] {
  return (conditionBlocks || [])
    .flatMap((block) => block.conditions ?? [])
    .map((condition) => {
      const { fieldName, operator, value, logicalOperator } = condition;

      let fieldDisplay = fieldName;
      let valueDisplay = value || '[enter value]';

      if (fieldName === 'fileType') {
        fieldDisplay = 'File Type';
        const docType = documentTypeOptions.find((o) => o.value === value);
        valueDisplay = docType
          ? docType.displayValue
          : value || '[select file type]';
      } else if (fieldName === 'fileSize') {
        fieldDisplay = 'File Size';
        const unitStr = condition.unit || 'MB';
        if (operator === 'between' && condition.secondaryValue) {
          valueDisplay = `${value || '0'} and ${
            condition.secondaryValue
          } ${unitStr}`;
        } else {
          valueDisplay = `${value || '0'} ${unitStr}`;
        }
      } else if (fieldName === 'memberCount') {
        fieldDisplay = 'Member Count';
        if (
          (operator === 'between' || operator === 'isNotBetween') &&
          condition.secondaryValue
        ) {
          valueDisplay = `${value || '0'} and ${condition.secondaryValue}`;
        } else {
          valueDisplay = value || '0';
        }
      } else if (condition.fieldInfoType === 'fileSharing') {
        fieldDisplay = 'File Sharing';
        if (fieldName === 'fileSharedToUserExternalToDomain') {
          valueDisplay = 'user external to the domain';
        } else if (fieldName === 'fileSharedToUserInternalToDomain') {
          valueDisplay = 'user internal to the domain';
        } else if (fieldName === 'fileIsSharedPublic') {
          valueDisplay = 'anyone with the link (public)';
        } else if (fieldName === 'fileSharedToSpecificUser') {
          valueDisplay = value ? `specific user: ${value}` : 'specific user';
        } else if (fieldName === 'fileIsSharedToDomain') {
          valueDisplay = value ? `domain: ${value}` : 'domain';
        } else {
          valueDisplay = fieldName;
        }
      } else {
        if (!fieldDisplay) {
          fieldDisplay = '[select field]';
        } else {
          const empMatch = employeeInfoFields.find(
            (f) => f.value === fieldName
          );
          if (empMatch) {
            fieldDisplay = empMatch.display;
          } else {
            const orgMatch = profileFieldOptions.find(
              (f) => f.value === fieldName
            );
            if (orgMatch) {
              fieldDisplay = orgMatch.display;
            } else if (fieldName.startsWith('customAttribute.')) {
              const parts = fieldName.split('.');
              fieldDisplay =
                parts.length >= 3
                  ? `${parts[1].replace(/_/g, ' ')} - ${parts
                      .slice(2)
                      .join('.')
                      .replace(/_/g, ' ')}`
                  : fieldName.replace(/_/g, ' ');
            } else {
              const calMatch = calendarSharingFields.find(
                (f) => f.value === fieldName
              );
              if (calMatch) {
                fieldDisplay = calMatch.display;
              } else {
                const staticDisplay = fieldNameMap[fieldName];
                if (staticDisplay) {
                  fieldDisplay = staticDisplay;
                }
              }
            }
          }
        }
      }

      const operatorLabels: Record<string, string> = {
        contains: 'contains',
        equals: 'equals',
        notContains: 'does not contain',
        notEquals: 'does not equal',
        startsWith: 'starts with',
        endsWith: 'ends with',
        larger: 'is greater than',
        smaller: 'is less than',
        greaterThan: 'is greater than',
        lessThan: 'is less than',
        greaterThanOrEqual: 'is greater than or equal to',
        lessThanOrEqual: 'is less than or equal to',
        between: 'is between',
        isNotBetween: 'is not between',
        isEmpty: 'is empty',
      };

      const operatorDisplay =
        operatorLabels[operator ?? ''] || operator || 'contains';

      return {
        text: `${fieldDisplay} ${operatorDisplay} "${valueDisplay}"`,
        logicalOperator: logicalOperator || 'and',
      };
    })
    .filter((item) => Boolean(item.text));
}
